import React, { useState } from "react";
import { DollarSign, Copy, CheckCircle, Mail, Phone, MapPin, Heart } from "lucide-react";

const SupportUsPage = () => {
  const [copied, setCopied] = useState(false);

  const acct = {
    name: "Rakai Community Health Initiative",
    number: "01640017875516",
    bank: "DFCU Bank",
  };

  const copyAccount = async () => {
    try {
      await navigator.clipboard.writeText(acct.number);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard may be blocked—silently ignore */
    }
  };

  return (
    <div className="support-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Funder banner */}
      <section className="banner" role="note" aria-label="Funder message">
        <div className="container">
          <div className="banner-inner">
            <Heart size={18} />
            <p className="m-0">
              <strong>Fund early action, not late rescue.</strong> Your transfer powers
              screening, linkage to care, and dignity—where outcomes change most.
            </p>
          </div>
        </div>
      </section>

      {/* Title */}
      <section className="title">
        <div className="container text-center">
          <h1 className="page-title">Support RACOHI</h1>
          <p className="page-subtitle">
            Thank you for partnering with communities in Rakai District, Uganda.
          </p>
        </div>
      </section>

      {/* Bank Transfer Details */}
      <section className="bank">
        <div className="container">
          <div className="bank-card">
            <div className="bank-head">
              <div className="bank-badge" aria-label="Bank Transfer Details">
                <DollarSign size={18} />
                <span className="ms-2 fw-semibold">Bank Transfer Details</span>
              </div>
            </div>

            <div className="bank-grid">
              <div className="bank-item">
                <div className="label">Account Name</div>
                <div className="value">{acct.name}</div>
              </div>

              <div className="bank-item with-action">
                <div className="label">Account Number</div>
                <div className="value mono">{acct.number}</div>
                <button
                  className={`btn-copy ${copied ? "ok" : ""}`}
                  onClick={copyAccount}
                  aria-label="Copy account number"
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  <span className="ms-2">{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>

              <div className="bank-item">
                <div className="label">Bank</div>
                <div className="value">{acct.bank}</div>
              </div>
            </div>

            <div className="fineprint">
              Need an official receipt or grant agreement? Email us and we’ll share documentation promptly.
            </div>
          </div>
        </div>
      </section>

      {/* Contacts */}
      <section className="contact">
        <div className="container">
          <div className="contact-card">
            <h3 className="mb-2">Contact to Confirm Your Gift</h3>
            <p className="contact-intro">
              Share your transfer reference so we can acknowledge your support and allocate it quickly.
            </p>

            <div className="contact-grid">
              <div className="contact-item">
                <div className="icon">
                  <Mail size={18} />
                </div>
                <div className="contact-content">
                  <div className="contact-title-item">Primary Email</div>
                  <div className="contact-info-item">
                    <a className="contact-link" href="mailto:nssewankambwe2023@gmail.com">
                      nssewankambwe2023@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-item">
                <div className="icon">
                  <Mail size={18} />
                </div>
                <div className="contact-content">
                  <div className="contact-title-item">Organization Email</div>
                  <div className="contact-info-item">
                    <a className="contact-link" href="mailto:rakaicommunityhealthinitiative@gmail.com">
                      rakaicommunityhealthinitiative@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-item">
                <div className="icon">
                  <Phone size={18} />
                </div>
                <div className="contact-content">
                  <div className="contact-title-item">Phone</div>
                  <div className="contact-info-item">
                    <a className="contact-link" href="tel:+256701816912">
                      +256 701 816 912
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-item">
                <div className="icon">
                  <MapPin size={18} />
                </div>
                <div className="contact-content">
                  <div className="contact-title-item">Location</div>
                  <div className="contact-info-item">
                    <span className="info-line">Rakai District, Uganda</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="cta-muted">
              <em>“Give once. Change outcomes for years.”</em>
            </div>
          </div>
        </div>
      </section>

      {/* Styles */}
      <style jsx>{`
        :root {
          --primary: #0b8fb8;
          --primary-dark: #0a6f90;
          --danger: #dc2626;
          --ink: #1f2937;
          --muted: #6b7280;
          --bg: #f8fafc;
          --card: #ffffff;
          --border: rgba(0, 0, 0, 0.08);
          --ring: rgba(11, 143, 184, 0.18);
        }

        .support-page { background: var(--bg); color: var(--ink); }

        /* Banner */
        .banner { background: var(--danger); color: #fff; padding: 10px 0; }
        .banner-inner {
          display: flex; align-items: center; justify-content: center;
          gap: 10px; text-align: center; font-weight: 600;
        }

        /* Title */
        .title { padding: 40px 0 10px; }
        .page-title { font-weight: 800; letter-spacing: -0.02em; }
        .page-subtitle { color: var(--muted); max-width: 720px; margin: 10px auto 0; }

        /* Bank */
        .bank { padding: 20px 0 10px; }
        .bank-card {
          background: var(--card); border: 1px solid var(--border);
          border-radius: 16px; padding: 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }
        .bank-head { display: flex; align-items: center; justify-content: space-between; }
        .bank-badge {
          display: inline-flex; align-items: center; padding: 6px 12px;
          border-radius: 999px; background: #f1fbfe; color: var(--primary);
          border: 1px solid var(--ring);
        }
        .bank-grid { display: grid; grid-template-columns: 1fr; gap: 14px; margin-top: 18px; }
        .bank-item {
          background: #fafafa; border: 1px solid var(--border);
          border-radius: 12px; padding: 14px; position: relative;
        }
        .bank-item.with-action { padding-right: 120px; }
        .label { font-size: 0.8rem; color: var(--muted); margin-bottom: 4px; }
        .value { font-weight: 700; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
        .btn-copy {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          display: inline-flex; align-items: center;
          background: #ffffff; border: 1px solid var(--border); color: var(--ink);
          padding: 8px 12px; border-radius: 10px; font-weight: 600; cursor: pointer;
        }
        .btn-copy:hover { border-color: var(--primary); box-shadow: 0 0 0 4px var(--ring); }
        .btn-copy.ok { background: #ecfdf5; border-color: #10b981; color: #065f46; }
        .fineprint { margin-top: 10px; color: var(--muted); font-size: 0.9rem; }

        /* Contact */
        .contact { padding: 10px 0 40px; }
        .contact-card {
          background: var(--card); border: 1px solid var(--border);
          border-radius: 16px; padding: 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }
        .contact-intro { color: var(--muted); margin-bottom: 18px; }

        /* --- LAYOUT FIXES FOR LONG EMAILS/PHONES --- */
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }
        .contact-item { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
        .contact-content { min-width: 0; } /* allow text to shrink/wrap */
        .icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: #f1fbfe; display: inline-flex; align-items: center; justify-content: center; color: var(--primary);
          flex-shrink: 0;
        }
        .contact-title-item { font-size: 1rem; font-weight: 700; color: var(--ink); white-space: nowrap; }
        .contact-info-item .contact-link,
        .contact-info-item .info-line {
          display: block;
          overflow-wrap: anywhere;
          word-break: break-word;
          white-space: normal;
          color: var(--primary-dark);
          text-decoration: none;
        }
        .contact-info-item .contact-link:hover { text-decoration: underline; }

        .cta-muted { margin-top: 12px; color: var(--muted); }

        @media (max-width: 576px) {
          .bank-item.with-action { padding-right: 12px; }
          .btn-copy { position: static; transform: none; width: 100%; justify-content: center; margin-top: 10px; }
        }
      `}</style>
    </div>
  );
};

export default SupportUsPage;
